import {useContext} from "react";
import {Box, HStack, Icon, Stack, Text} from "@chakra-ui/react";
import {XMarkIcon} from "@heroicons/react/20/solid";
import type {ReviewAction, ReviewStatus} from "platformed-client/types";
import {OwnerAvatar} from "../../../components/OwnerAvatar";
import {IconButton} from "@chakra-ui/react";
import useTranslate from "platformed-components/i18n/useTranslate";
import {Tag} from "../../../components/ui/tag";
import T from "platformed-components/i18n/T";
import {REVIEW_STATUS_MAP} from "../ReviewStatus";
import ReviewActionButton from "./ReviewActionButton";
import type {ReviewItem} from ".";
import {ReviewerNoteContext} from "./reviewerNote";

export const ReviewStatusText = ({status}: {status: ReviewStatus}) => {
  const info = REVIEW_STATUS_MAP[status];
  return (
    <HStack gap={1} color={`${info.color ?? "gray"}.600`} flexShrink={0}>
      {info.icon && (
        <Icon asChild boxSize="13px">
          {info.icon}
        </Icon>
      )}
      <Text fontSize="xs" fontWeight="medium">
        {info.text}
      </Text>
    </HStack>
  );
};

/**
 * A single reviewer: who they are, where their review has got to, and the actions open to the
 * viewer. Removal lives in the role's picker, which carries the confirmation.
 */
export const ReviewerRow = ({
  review,
  reviewItem,
  onRemove,
  pendingRemoval = false,
}: {
  review: ReviewAction;
  reviewItem: ReviewItem;
  /** Offered only for reviewers no role claims, which the pickers can't reach. */
  onRemove?: () => void;
  /** Queued for removal on a surface that saves later; shown struck through until then. */
  pendingRemoval?: boolean;
}) => {
  const t = useTranslate();
  const note = useContext(ReviewerNoteContext)?.(review);
  return (
    <Stack gap={1} py={1.5} opacity={pendingRemoval ? 0.5 : 1}>
      <HStack gap={3} align="center">
        <HStack flex={1} minW={0} gap={2}>
          <OwnerAvatar owner={review.reviewer.owner_payload} />
          {pendingRemoval ? (
            <Tag size="sm" colorPalette="red">
              <T keyName="c.Will_be_removed" />
            </Tag>
          ) : (
            <ReviewStatusText status={review.review_status} />
          )}
        </HStack>
        <ReviewActionButton review={review} reviewItem={reviewItem} />
        {onRemove && (
          <IconButton
            aria-label={t("c.Remove_reviewer")}
            size="2xs"
            variant="ghost"
            colorPalette="gray"
            onClick={onRemove}
          >
            <Icon asChild boxSize="14px">
              <XMarkIcon />
            </Icon>
          </IconButton>
        )}
      </HStack>
      {note && (
        <Text ml={8} fontSize="xs" color="fg.muted">
          {note}
        </Text>
      )}
      {review.comment && (
        <Box ml={8} bg="gray.50" border="1px solid {colors.gray.100}" p={3} rounded="md">
          <Text fontSize="sm">{review.comment}</Text>
        </Box>
      )}
    </Stack>
  );
};
